/**
 * notchesCalendarReadService — client for the calendar-notches trade-insights
 * read API.
 *
 * Backed by GET /n2/api/calendar-notches/read?date=YYYY-MM-DD on the main
 * auth API Gateway (same host as notchesService.ts). Premier-gated: caller
 * must send a Bearer JWT, and the backend additionally requires
 * `calendarNotchesEnabled === true` on the user record (see
 * api/auth/src/notchesGetCalendarRead.js). Both gates return 403, not an
 * empty payload, so callers should surface `err` distinctly from "no data
 * today" (which is `today: null` with HTTP 200).
 */

import axios from 'axios';

// Public read — prefer PUBLIC_API_URL so a local Stripe-test build still
// hits prod for the calendar-read Lambda. See briefingsService for the
// same rationale.
const API_BASE_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CalendarReadEvent {
  name: string;
  event_ts: string;
  impact: string;
}

export interface CalendarReadTradeLeg {
  symbol: string;
  expiration: string;
  strike: number;
  contract_symbol: string;
}

export interface CalendarReadTradeIllustration {
  structure: 'short_calendar' | 'long_calendar';
  short_leg: CalendarReadTradeLeg;
  long_leg: CalendarReadTradeLeg;
  atm_ref: number;
  es_spot?: number;
}

export interface CalendarReadConfidence {
  tier: 'LOW' | 'MEDIUM' | 'HIGH' | 'NO_SIGNAL';
  score: number;
}

export interface CalendarReadRow {
  date: string;
  commentaryId: string;
  generated_at: string;
  hero_summary: string;
  commentary_markdown: string;
  confidence: CalendarReadConfidence;
  signal_source: any;
  events: CalendarReadEvent[];
  trade_illustration: CalendarReadTradeIllustration | null;
  tags: string[];
  llm_model: string;
}

export interface CalendarReadResponse {
  today: CalendarReadRow | null;
  recent: CalendarReadRow[];
}

// ─── API ────────────────────────────────────────────────────────────────────

export const notchesCalendarReadService = {
  getRead: async (token: string, date?: string): Promise<CalendarReadResponse> => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    const response = await api.get<CalendarReadResponse>('/n2/api/calendar-notches/read', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};
